/**
 * The catalog of individually searchable settings.
 *
 * Settings search has to find a *setting*, not a page. Knowing that "Glass
 * opacity" exists is not the hard part — remembering that it lives under
 * Appearance rather than Settings is, and that is exactly the knowledge a
 * search over page titles cannot supply. So the unit indexed here is the row.
 *
 * **The catalog is the source of the rendered title, not a copy of it.** Panels
 * spread `searchableSetting("appearance-glass-opacity")` onto their row instead of writing
 * the string twice, which also gives the row the anchor id that
 * `scrollToSettingsTarget` needs. That direction matters: a catalog that merely
 * described the panels would rot silently the first time someone renamed a row,
 * and the failure mode — a setting that exists but cannot be found — looks
 * identical to a setting that was never indexed. Sourcing the title from here
 * makes the drift unrepresentable, and `settingsSearch.test.ts` holds the line
 * for rows that have not adopted it yet.
 *
 * @module
 */

import { APPEARANCE_SETTINGS_SEARCH_ROWS } from "./settingsSearchAppearanceRows";
import { NOTIFICATION_SETTINGS_SEARCH_ROWS } from "./settingsSearchNotificationRows";
import { WORKFLOW_SETTINGS_SEARCH_ROWS } from "./settingsSearchWorkflowRows";

/**
 * Settings destinations reachable from the sidebar nav.
 *
 * Deliberately narrower than the router's `SettingsSectionPath`, which still
 * carries legacy paths (`/settings/chat`, `/settings/editor`, `/settings/system`)
 * whose panels render but which the nav no longer links. Indexing those would
 * hand people a result that navigates somewhere they cannot get back to, and
 * would quietly un-retire pages by making them findable again — a product
 * decision, not a search one.
 */
/**
 * Page labels in sidebar order.
 *
 * A result is useless without saying where it lives, so every result renders
 * its page label as a subtitle. These are the same strings the nav shows, and
 * `settingsSearch.test.ts` asserts the two records agree rather than letting a
 * renamed nav item silently disagree with its own search results.
 */
export const SETTINGS_SECTION_LABELS = {
  "/settings/general": "General",
  "/settings/appearance": "Appearance",
  "/settings/keybindings": "Keybindings",
  "/settings/providers": "Providers",
  // Agents has no upstream counterpart -- t3code has no multi-agent orchestration --
  // so it sits next to Providers, the destination it is most often confused with.
  "/settings/agents": "Agents",
  "/settings/cmmd": "Integrations",
  "/settings/git": "Source Control",
  "/settings/environments": "Connections",
  // Its own destination rather than a section of Connections. The two answer
  // different questions -- "which machine runs my agent" versus "what may that
  // agent open, and what does it keep" -- and the browser half is the larger of
  // the two, so nesting it buried five rows of permissions and domain lists
  // under a page whose title gave no hint they were there.
  "/settings/browser": "Browser",
  "/settings/archived": "Archive",
  // Operator tooling, not preference tuning: bottom of the rail, above Log out.
  "/settings/diagnostics": "Diagnostics",
} as const;

export type SettingsSearchPath = keyof typeof SETTINGS_SECTION_LABELS;

/**
 * Destinations that only exist when a feature flag is on.
 *
 * The grouped rail carried this in `getVisibleSettingsNavGroups`, which filtered
 * a hand-written "Integrations" group. A derived rail has no groups to filter,
 * so the gate has to live beside the labels it hides — otherwise flattening the
 * rail would quietly show Integrations to everyone.
 */
export const SETTINGS_SECTIONS_REQUIRING_CMMD_SYNC: ReadonlySet<SettingsSearchPath> = new Set([
  "/settings/cmmd",
]);

export interface SettingsSearchItem {
  /** Also the DOM anchor id, so a result can scroll to its row. */
  readonly id: string;
  /** The exact heading the row renders. Changing it here changes the UI. */
  readonly title: string;
  readonly to: SettingsSearchPath;
  /**
   * Words a person would plausibly type that the title does not contain.
   *
   * Used sparingly and only where the gap is real: nobody searching for dark
   * mode types "Colour scheme", and the British spelling means half our users
   * would not find it by title even if they did know the name.
   */
  readonly keywords?: ReadonlyArray<string>;
  /**
   * Its page only appears in the nav when CMMD sync is enabled, so a result
   * would otherwise navigate to a page with no way back to it.
   */
  readonly requiresCmmdSync?: boolean;
  /**
   * Its row only renders in the desktop app, so a browser result would land on
   * an anchor that is not there. `KeyboardSettingsSection` and the About
   * update-channel row both return null when `!isElectron`.
   */
  readonly desktopOnly?: boolean;
  /**
   * Why this entry has no rendered anchor yet, so the result navigates to its
   * page without scrolling to the row.
   *
   * Every entry must either be bound -- its id spread onto the row via
   * `searchableSetting` -- or say here why it is not. `settingsSearch.test.ts`
   * reads the panel sources and enforces exactly that, so the set can shrink
   * but cannot silently grow. Tracked to empty in FOR-2604.
   */
  readonly unanchoredReason?: string;
  /**
   * Why this entry's anchor may not be MOUNTED even though it exists in source.
   *
   * Distinct from `unanchoredReason`, and deliberately a second field: that one
   * means "no id anywhere", this one means "the id is there but the element is
   * behind a toggle or a tab". Keeping them apart lets each be tested in the
   * direction that can rot -- an unanchored reason must correspond to an
   * unbound id, a conditional one to a bound id -- so neither survives the fix
   * it describes.
   */
  readonly conditionalTargetReason?: string;
}

export const SETTINGS_SEARCH_ITEMS = [
  // Profile
  {
    id: "profile-email",
    title: "Email",
    to: "/settings/general",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  {
    id: "profile-username",
    title: "Username",
    to: "/settings/general",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },

  // Appearance rows live in settingsSearchAppearanceRows.ts (see its header).
  ...APPEARANCE_SETTINGS_SEARCH_ROWS,
  // Keybindings
  {
    id: "keybindings-shortcuts",
    title: "Keyboard shortcuts",
    to: "/settings/keybindings",
    keywords: ["keybindings", "hotkeys", "shortcut", "rebind", "chord"],
  },
  {
    id: "typography-interface-font",
    title: "Interface font",
    to: "/settings/appearance",
    keywords: ["ui font size", "text size"],
  },
  {
    id: "typography-monospace-font",
    title: "Monospace font",
    to: "/settings/appearance",
    keywords: ["code font", "diff font"],
  },
  {
    id: "typography-prompt-font",
    title: "Prompt font",
    to: "/settings/appearance",
    keywords: ["composer font"],
    conditionalTargetReason:
      "Mounted only when the Appearance page's advanced typography toggle is on.",
  },
  {
    id: "typography-terminal-font",
    title: "Terminal font",
    to: "/settings/appearance",
    conditionalTargetReason:
      "Mounted only when the Appearance page's advanced typography toggle is on.",
  },
  {
    id: "typography-font-smoothing",
    title: "Font smoothing",
    to: "/settings/appearance",
    keywords: ["antialiasing"],
    conditionalTargetReason:
      "Mounted only when the Appearance page's advanced typography toggle is on.",
  },
  { id: "typography-word-wrap", title: "Word wrap", to: "/settings/appearance" },

  // About
  {
    id: "about-update-channel",
    title: "Update channel",
    to: "/settings/general",
    keywords: ["beta", "nightly", "stable", "version"],
    desktopOnly: true,
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },

  // Settings (general)
  {
    id: "general-theme",
    title: "Theme",
    to: "/settings/general",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  {
    id: "general-time-format",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
    title: "Time format",
    to: "/settings/general",
    keywords: ["12 hour", "24 hour", "clock"],
  },
  {
    id: "general-keybindings",
    title: "Keybindings",
    to: "/settings/general",
    // Rendered inside `{isElectron ? <SettingsSection title="Advanced"> ...`
    desktopOnly: true,
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  {
    id: "general-diagnostics",
    title: "Diagnostics",
    to: "/settings/diagnostics",
    keywords: ["environment", "health", "sprite"],
  },
  {
    id: "general-log-directory",
    title: "Log directory",
    to: "/settings/general",
    keywords: ["logs", "folder", "export"],
  },
  ...NOTIFICATION_SETTINGS_SEARCH_ROWS,

  // Providers
  {
    id: "providers",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
    title: "Providers",
    to: "/settings/providers",
    keywords: ["anthropic", "openai", "model access", "api key", "sign in"],
    desktopOnly: true,
  },

  ...WORKFLOW_SETTINGS_SEARCH_ROWS,

  // Connections
  {
    id: "connections-execution-targets",
    title: "Connections",
    to: "/settings/environments",
    keywords: ["runtime", "cloud vm", "local"],
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  // Browser
  {
    id: "browser-use",
    title: "Browser use",
    to: "/settings/browser",
  },
  {
    id: "browser-browsing-data",
    title: "Browsing data",
    to: "/settings/browser",
    keywords: ["cookies", "clear"],
  },
  {
    id: "browser-annotation-screenshots",
    title: "Annotation screenshots",
    to: "/settings/browser",
  },
  {
    id: "browser-session-retention",
    title: "Session retention",
    to: "/settings/browser",
  },
  {
    id: "browser-approval",
    title: "Approval",
    to: "/settings/browser",
    keywords: ["permissions", "ask"],
  },
  {
    id: "browser-never-open",
    title: "Never open",
    to: "/settings/browser",
    keywords: ["blocked domains", "blocklist"],
  },
  {
    id: "browser-open-without-asking",
    title: "Open without asking",
    to: "/settings/browser",
    keywords: ["allowed domains", "allowlist"],
  },

  // Threads
  {
    id: "threads-new-threads",
    title: "New threads",
    to: "/settings/agents",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  {
    id: "threads-assistant-output",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
    title: "Assistant output",
    to: "/settings/agents",
    keywords: ["streaming"],
  },
  {
    id: "threads-archive-confirmation",
    title: "Archive confirmation",
    to: "/settings/agents",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  {
    id: "threads-delete-confirmation",
    title: "Delete confirmation",
    to: "/settings/agents",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },

  // Composer
  {
    id: "composer-build-plan-toggle",
    title: "Build/Plan mode toggle",
    to: "/settings/agents",
    keywords: ["mode switch"],
  },

  // Git
  {
    id: "git-text-generation-model",
    title: "Text generation model",
    to: "/settings/git",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
  },
  {
    id: "git-co-author",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
    title: "Co-author commits with Forge",
    to: "/settings/git",
    keywords: ["commit trailer", "attribution"],
  },
  {
    id: "git-write-identity",
    unanchoredReason: "Row lives in SettingsPanels.tsx, serialized behind scope:settings.",
    title: "GitHub write identity",
    to: "/settings/git",
    keywords: ["push", "author"],
  },

  // Archived
  {
    id: "archived",
    title: "Archived",
    to: "/settings/archived",
    keywords: ["restore", "recover thread"],
  },

  // CMMD Sync — gated, see `requiresCmmdSync`.
  {
    id: "cmmd-auto-create-tasks",
    title: "Auto-create tasks",
    to: "/settings/cmmd",
    requiresCmmdSync: true,
  },
  {
    id: "cmmd-push-local-project",
    title: "Push local project",
    to: "/settings/cmmd",
    requiresCmmdSync: true,
  },
  {
    id: "cmmd-sync-task-status",
    title: "Sync task status",
    to: "/settings/cmmd",
    requiresCmmdSync: true,
  },

  // Diagnostics
  {
    id: "diagnostics-target",
    title: "Environment",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-codex",
    title: "Codex delivery",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-authority",
    title: "Provider authority",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-lifecycle",
    title: "Lifecycle preflight",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-events",
    title: "Runtime events",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-tools",
    title: "Tools and services",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-processes",
    title: "Processes",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
  {
    id: "diagnostics-traces",
    title: "Traced failures",
    to: "/settings/diagnostics",
    conditionalTargetReason: "Mounted only when its Diagnostics tab is the active one.",
  },
] as const satisfies ReadonlyArray<SettingsSearchItem>;

export type SettingsSearchItemId = (typeof SETTINGS_SEARCH_ITEMS)[number]["id"];

const SEARCH_ITEMS_BY_ID = Object.fromEntries(
  SETTINGS_SEARCH_ITEMS.map((item) => [item.id, item]),
) as Readonly<Record<SettingsSearchItemId, SettingsSearchItem>>;

/**
 * The `id` and `title` props for the row a search item points at.
 *
 * Panels spread this instead of restating the string, which is what keeps the
 * catalog and the rendered settings from drifting apart — and hands the row the
 * anchor id a result needs to scroll to it, for free.
 */
export function searchableSetting(id: SettingsSearchItemId): {
  readonly id: string;
  readonly title: string;
} {
  const { id: anchorId, title } = SEARCH_ITEMS_BY_ID[id];
  return { id: anchorId, title };
}
